import type { AppThemeConfigType } from '@unifyapps/defs/types/interface';
import { BorderVariants } from '@unifyapps/defs/types/interface';
import { createContext, useContext } from 'react';
import type { ModuleValues } from '../../../../consts/modules';
import type { ColorConfigEnum } from '../components/ColorEditor/types';
import type { TypographyHierarchyType } from '../components/TypographyEditor/types';

export const INITIAL_APP_THEME: AppThemeConfigType = {
  border: BorderVariants.Rounded,
};

export interface AppThemeConfigContextValue {
  theme: AppThemeConfigType | undefined;
  setTheme: (theme: AppThemeConfigType) => void;
  setBorderConfig: (variant: BorderVariants) => void;
  setTypographyConfig: (
    id: TypographyHierarchyType,
    typographyConfig: Record<string, number>,
  ) => void;
  setColorConfig: ({
    configType,
    id,
    token,
    hexValue,
  }: {
    configType?: ColorConfigEnum;
    id: string;
    token: string;
    hexValue: string;
  }) => void;
  setFontFamilyConfig: (fontFamilyConfig: Record<string, string>) => void;
  dirtyTokens: Set<string>;
  resetColorToken: ({
    configType,
    id,
    token,
  }: {
    configType?: ColorConfigEnum;
    id: string;
    token: string;
  }) => void;
  resetThemeChanges: () => void;
  resetThemeToDefault: () => void;
  module?: ModuleValues;
}

const AppThemeConfigContext = createContext<AppThemeConfigContextValue | null>(null);

const useAppThemeConfigContext = (): AppThemeConfigContextValue => {
  const context = useContext(AppThemeConfigContext);

  if (!context) {
    throw new Error('useAppThemeConfigContext must be used within a AppThemeConfigContextProvider');
  }

  return context;
};

export { useAppThemeConfigContext };
export default AppThemeConfigContext;
